import { useEffect, useRef, useState } from "react";
import { animate, AnimatePresence, motion } from "framer-motion";
import Sidebar from "./components/Sidebar";
import About from "./sections/About";
import Experience from "./sections/Experience";
import Education from "./sections/Education";
import Skills from "./sections/Skills";
import Certifications from "./sections/Certifications";
import Projects from "./sections/Projects";

function App() {
  const [activeSection, setActiveSection] = useState("");
  const [isMobileSidebar, setIsMobileSidebar] = useState(false);

  const sectionRefs = {
    about: useRef(null),
    experience: useRef(null),
    education: useRef(null),
    skills: useRef(null),
    certifications: useRef(null),
    projects: useRef(null),
  };

  const mainRef = useRef(null);

  // Lock highlights during programmatic scrolling
  const isScrollingRef = useRef(false);

  // ----- Stable highlight on scroll (center-line method, no flicker) -----
  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;

    let ticking = false;

    const computeActive = () => {
      if (isScrollingRef.current) {
        ticking = false;
        return;
      }

      const rootRect = main.getBoundingClientRect();
      // Probe line slightly above center so the next section doesn't light too early
      const probeY = rootRect.top + rootRect.height * 0.35;

      let bestId = null;
      let bestDist = Infinity;

      for (const [id, ref] of Object.entries(sectionRefs)) {
        const el = ref.current;
        if (!el) continue;
        const rect = el.getBoundingClientRect();

        // If the probe line is inside this section, it's our winner
        if (rect.top <= probeY && rect.bottom >= probeY) {
          bestId = id;
          bestDist = 0;
          break;
        }

        // Otherwise pick the section whose center is closest to the probe line
        const center = rect.top + rect.height / 2;
        const dist = Math.abs(center - probeY);
        if (dist < bestDist) {
          bestDist = dist;
          bestId = id;
        }
      }

      if (bestId && bestId !== activeSection) {
        setActiveSection(bestId);
      }
      ticking = false;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(computeActive);
    };

    // Initial compute and listener
    computeActive();
    main.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      main.removeEventListener("scroll", onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  const scrollToSection = (id) => {
    const section = sectionRefs[id]?.current;
    const main = mainRef.current;

    if (section && main) {
      // Lock highlight on the clicked item during the animation
      isScrollingRef.current = true;
      setActiveSection(id);

      const sectionTop = section.offsetTop;
      const mainTop = main.offsetTop;

      let offset;
      if (window.innerWidth < 768) {
        if (id === "skills") offset = 20;
        else if (id === "about") offset = 5;
        else if (id === "experience") offset = 5;
        else if (id === "education") offset = 5;
        else if (id === "certifications") offset = 5;
        else if (id === "projects") offset = 5;
        else offset = 100;
      } else {
        if (id === "skills") offset = -30;
        else if (id === "about") offset = 30;
        else if (id === "experience") offset = -30;
        else if (id === "education") offset = 100;
        else if (id === "certifications") offset = 80;
        else if (id === "projects") offset = -10;
        else offset = 140;
      }

      const targetScrollTop = sectionTop - mainTop - offset;

      animate(main.scrollTop, targetScrollTop, {
        duration: 0.7,
        ease: "easeInOut",
        onUpdate: (latest) => {
          main.scrollTop = latest;
        },
        onComplete: () => {
          // Release the lock; the scroll listener will take over
          isScrollingRef.current = false;
        },
      });

      if (window.innerWidth < 768) {
        setIsMobileSidebar(false);
      }
    }
  };

  return (
    <div className="md:flex min-h-screen bg-white text-gray-800 font-sans dark:bg-gray-900 dark:text-white">
      {/* Mobile Top Bar */}
      <div className="w-full md:hidden">
        <div className="w-full fixed top-0 left-0 bg-blue-800 z-50 p-4 flex justify-end items-center border-none shadow-none">
          <button
            onClick={() => setIsMobileSidebar(!isMobileSidebar)}
            className="text-white text-xl focus:outline-none"
          >
            ☰
          </button>
        </div>

        {/* Animated Mobile Sidebar */}
        <AnimatePresence>
          {isMobileSidebar && (
            <motion.div
              className="fixed top-0 left-0 w-full h-[90vh] bg-blue-800 z-40 rounded-b-xl shadow-lg"
              initial={{ y: "-100%" }}
              animate={{ y: 0 }}
              exit={{ y: "-100%" }}
              transition={{ duration: 0.8, ease: [0.25, 0.8, 0.25, 1] }}
            >
              <div className="pt-8 md:pt-20 h-full overflow-y-auto">
                <Sidebar
                  activeSection={activeSection}
                  scrollToSection={scrollToSection}
                  isMobile={true}
                  onCloseMobileMenu={() => setIsMobileSidebar(false)}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Desktop Sidebar */}
      <div className="hidden md:block">
        <Sidebar
          activeSection={activeSection}
          scrollToSection={scrollToSection}
        />
      </div>

      {/* Main Content */}
      <main
        ref={mainRef}
        className="flex-1 px-4 md:px-8 py-20 md:py-16 space-y-24 overflow-y-auto h-screen bg-white dark:bg-gray-900"
      >
        <section
          id="about"
          ref={sectionRefs.about}
          className="scroll-mt-24 min-h-screen"
        >
          <About />
        </section>
        <section
          id="experience"
          ref={sectionRefs.experience}
          className="scroll-mt-36 min-h-screen"
        >
          <Experience />
        </section>
        <section
          id="education"
          ref={sectionRefs.education}
          className="scroll-mt-24 min-h-screen"
        >
          <Education />
        </section>
        <section
          id="skills"
          ref={sectionRefs.skills}
          className="scroll-mt-36 min-h-screen"
        >
          <Skills />
        </section>
        <section
          id="certifications"
          ref={sectionRefs.certifications}
          className="scroll-mt-24 min-h-screen"
        >
          <Certifications />
        </section>
        <section
          id="projects"
          ref={sectionRefs.projects}
          className="scroll-mt-24 min-h-screen"
        >
          <Projects />
        </section>
      </main>
    </div>
  );
}

export default App;
